import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, CheckCircle2 } from 'lucide-react';

export type ListingType = 'property' | 'commercial' | 'plot' | 'project';

export interface PickedListing {
  id: string;
  type: ListingType;
  title: string;
  city?: string | null;
  reference_code?: string | null;
  image_url?: string | null;
}

interface Props {
  value: PickedListing | null;
  onChange: (listing: PickedListing | null) => void;
  initialType?: ListingType;
  onTypeChange?: (t: ListingType) => void;
}

export function ListingPicker({ value, onChange, initialType = 'property', onTypeChange }: Props) {
  const [type, setType] = useState<ListingType>(initialType);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<PickedListing[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchItems = async () => {
      setLoading(true);
      try {
        if (type === 'project') {
          const { data: projects } = await supabase
            .from('projects')
            .select('id, name, city, reference_code')
            .eq('approval_status', 'approved')
            .order('created_at', { ascending: false })
            .limit(50);
          const ids = (projects || []).map(p => p.id);
          const { data: imgs } = ids.length
            ? await supabase.from('project_images').select('project_id, url, is_primary').in('project_id', ids)
            : { data: [] as any[] };
          const imgMap = new Map<string, string>();
          (imgs || []).forEach((i: any) => {
            if (!imgMap.has(i.project_id) || i.is_primary) imgMap.set(i.project_id, i.url);
          });
          if (!active) return;
          setItems((projects || []).map((p: any) => ({
            id: p.id, type: 'project',
            title: p.name?.en || p.name?.am || 'Untitled Project',
            city: p.city, reference_code: p.reference_code,
            image_url: imgMap.get(p.id) || null,
          })));
        } else if (type === 'plot') {
          const { data } = await supabase
            .from('plots')
            .select('id, title, city, reference_code')
            .eq('approval_status', 'approved')
            .order('created_at', { ascending: false })
            .limit(50);
          const ids = (data || []).map(p => p.id);
          const { data: imgs } = ids.length
            ? await supabase.from('plot_images').select('plot_id, url, is_primary').in('plot_id', ids)
            : { data: [] as any[] };
          const imgMap = new Map<string, string>();
          (imgs || []).forEach((i: any) => {
            if (!imgMap.has(i.plot_id) || i.is_primary) imgMap.set(i.plot_id, i.url);
          });
          if (!active) return;
          setItems((data || []).map((p: any) => ({
            id: p.id, type: 'plot',
            title: p.title?.en || p.title?.am || 'Untitled Plot',
            city: p.city, reference_code: p.reference_code,
            image_url: imgMap.get(p.id) || null,
          })));
        } else {
          // property or commercial
          let q = supabase
            .from('properties')
            .select('id, title, city, reference_code, property_type')
            .eq('approval_status', 'approved')
            .order('created_at', { ascending: false })
            .limit(50);
          if (type === 'commercial') q = q.eq('property_type', 'commercial' as any);
          else q = q.neq('property_type', 'commercial' as any);
          const { data } = await q;
          const ids = (data || []).map((p: any) => p.id);
          const { data: imgs } = ids.length
            ? await supabase.from('property_images').select('property_id, url, is_primary').in('property_id', ids)
            : { data: [] as any[] };
          const imgMap = new Map<string, string>();
          (imgs || []).forEach((i: any) => {
            if (!imgMap.has(i.property_id) || i.is_primary) imgMap.set(i.property_id, i.url);
          });
          if (!active) return;
          setItems((data || []).map((p: any) => ({
            id: p.id, type,
            title: p.title?.en || p.title?.am || 'Untitled',
            city: p.city, reference_code: p.reference_code,
            image_url: imgMap.get(p.id) || null,
          })));
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchItems();
    return () => { active = false; };
  }, [type]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      i.title.toLowerCase().includes(q) ||
      i.city?.toLowerCase().includes(q) ||
      i.reference_code?.toLowerCase().includes(q)
    );
  }, [items, search]);

  const handleTypeChange = (t: string) => {
    const nt = t as ListingType;
    setType(nt);
    onTypeChange?.(nt);
  };

  if (value) {
    return (
      <div className="border rounded-md p-3 bg-muted/30 flex items-center gap-3">
        {value.image_url ? (
          <img src={value.image_url} alt="" className="h-12 w-12 rounded object-cover" />
        ) : (
          <div className="h-12 w-12 rounded bg-muted" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
            <p className="text-sm font-medium truncate">{value.title}</p>
          </div>
          <p className="text-xs text-muted-foreground">
            <span className="capitalize">{value.type}</span>
            {value.city && ` • ${value.city}`}
            {value.reference_code && ` • ${value.reference_code}`}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => onChange(null)}>Change</Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Tabs value={type} onValueChange={handleTypeChange}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="property">Property</TabsTrigger>
          <TabsTrigger value="commercial">Commercial</TabsTrigger>
          <TabsTrigger value="plot">Plot</TabsTrigger>
          <TabsTrigger value="project">Project</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filter by title, city, or reference code..."
          className="pl-8"
        />
      </div>

      <div className="border rounded-md max-h-64 overflow-y-auto">
        {loading ? (
          <div className="p-2 space-y-2">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            {items.length === 0 ? `No approved ${type} listings yet` : 'No matches'}
          </p>
        ) : (
          filtered.map(item => (
            <button
              key={item.id}
              type="button"
              onClick={() => onChange(item)}
              className="w-full text-left flex items-center gap-3 p-2 hover:bg-muted border-b last:border-b-0 transition-colors"
            >
              {item.image_url ? (
                <img src={item.image_url} alt="" className="h-10 w-10 rounded object-cover shrink-0" />
              ) : (
                <div className="h-10 w-10 rounded bg-muted shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{item.title}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {item.city || '—'}
                  {item.reference_code && ` • ${item.reference_code}`}
                </p>
              </div>
              <Badge variant="secondary" className="text-[10px] capitalize shrink-0">{item.type}</Badge>
            </button>
          ))
        )}
      </div>
      <p className="text-xs text-muted-foreground">{filtered.length} listing{filtered.length === 1 ? '' : 's'} shown</p>
    </div>
  );
}
